import { useState } from "react";
import axios from "axios";
import { API, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, CalendarClock, User, Camera, ClipboardList } from "lucide-react";
import { toast } from "sonner";

export const PLAN_STATUS = {
  not_started: { label: "Not started", color: "#94A3B8", bg: "rgba(148,163,184,0.14)" },
  in_progress: { label: "In progress", color: "#C5A24A", bg: "rgba(212,168,75,0.14)" },
  blocked: { label: "Blocked", color: "#EA580C", bg: "rgba(234,88,12,0.14)" },
  complete: { label: "Complete", color: "#10B981", bg: "rgba(16,185,129,0.14)" },
};

const inputCls = "w-full bg-[#121212] border border-white/10 rounded px-3 py-2 text-sm text-foreground";

function PlanCard({ plan, token, pin, pinOk, onRefresh }) {
  const st = PLAN_STATUS[plan.status] || PLAN_STATUS.not_started;
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [adding, setAdding] = useState(false);

  const setStatus = async (status) => {
    try { await axios.put(`${API}/share/${token}/action-plans/${plan.id}`, { pin, status }); onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("pin", pin); fd.append("file", f);
      const { data } = await axios.post(`${API}/share/${token}/upload`, fd);
      setPhotos((p) => [...p, data.path]);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const addUpdate = async () => {
    if (!note.trim() && photos.length === 0) return toast.error("Add a note or photo");
    setBusy(true);
    try {
      await axios.post(`${API}/share/${token}/action-plans/${plan.id}/updates`, { pin, note, photos, author: plan.assignee_name || "" });
      setNote(""); setPhotos([]); setAdding(false); toast.success("Progress added"); onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 bg-[#0A0A0A] border border-white/10 rounded p-3" data-testid={`plan-${plan.id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium">{plan.title}</p>
        {pinOk ? (
          <select value={plan.status} onChange={(e) => setStatus(e.target.value)} data-testid={`plan-status-${plan.id}`}
            className="text-[11px] rounded px-2 py-1 bg-[#121212] border border-white/15" style={{ color: st.color }}>
            {Object.keys(PLAN_STATUS).map((k) => <option key={k} value={k} style={{ color: "#fff", background: "#121212" }}>{PLAN_STATUS[k].label}</option>)}
          </select>
        ) : (
          <span className="text-[10px] px-2 py-1 rounded shrink-0" style={{ background: st.bg, color: st.color }}>{st.label}</span>
        )}
      </div>
      {plan.description && <p className="text-xs text-muted-foreground mt-1">{plan.description}</p>}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-muted-foreground">
        {plan.assignee_name && <span className="flex items-center gap-1"><User className="w-3 h-3" /> {plan.assignee_name}</span>}
        {plan.target_date && <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {plan.target_date}</span>}
      </div>

      {(plan.updates || []).length > 0 && (
        <div className="mt-3 space-y-2 border-l border-white/10 pl-3">
          {plan.updates.map((u) => (
            <div key={u.id} className="text-xs" data-testid={`update-${u.id}`}>
              <p className="text-muted-foreground">{(u.created_at || "").slice(0, 10)}{u.author ? ` · ${u.author}` : ""}</p>
              {u.note && <p className="text-foreground mt-0.5">{u.note}</p>}
              {(u.photos || []).length > 0 && (
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  {u.photos.map((ph) => <img key={ph} src={fileUrl(ph)} alt="progress" className="w-14 h-14 object-cover rounded border border-white/10" />)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {pinOk && (adding ? (
        <div className="mt-3 space-y-2">
          <textarea placeholder="Progress note" value={note} onChange={(e) => setNote(e.target.value)} className={`${inputCls} min-h-[50px]`} data-testid={`update-note-${plan.id}`} />
          {photos.length > 0 && <div className="flex gap-1.5 flex-wrap">{photos.map((ph) => <img key={ph} src={fileUrl(ph)} alt="" className="w-12 h-12 object-cover rounded border border-white/10" />)}</div>}
          <div className="flex items-center gap-2">
            <label className="text-xs px-2.5 py-1.5 rounded bg-[#121212] border border-white/10 cursor-pointer hover:bg-[#1E1E1E] flex items-center gap-1">
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />} Photo
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid={`update-photo-${plan.id}`} />
            </label>
            <Button size="sm" onClick={addUpdate} disabled={busy} className="bg-[#C5A24A] hover:bg-[#A8863A] text-black text-xs" data-testid={`save-update-${plan.id}`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add progress"}</Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)} className="text-xs">Cancel</Button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} className="mt-3 text-xs text-[#C5A24A] hover:underline flex items-center gap-1" data-testid={`add-update-${plan.id}`}><Plus className="w-3 h-3" /> Add progress update</button>
      ))}
    </div>
  );
}

export default function RemediationPlanner({ token, pin, pinOk, onNeedPin, defectId = null, plans, onRefresh }) {
  const mine = plans.filter((p) => (p.defect_id || null) === defectId);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assignee_name: "", assignee_email: "", target_date: "" });

  const createPlan = async () => {
    if (!form.title.trim()) return toast.error("Give the plan a title");
    setBusy(true);
    try {
      await axios.post(`${API}/share/${token}/action-plans`, { pin, defect_id: defectId, ...form });
      setForm({ title: "", description: "", assignee_name: "", assignee_email: "", target_date: "" });
      setCreating(false); toast.success("Action plan created"); onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border-t border-white/10 pt-4" data-testid={`remediation-${defectId || "project"}`}>
      <div className="flex items-center justify-between">
        <p className="label-caps text-[#C5A24A] flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" /> Remediation plan{mine.length > 1 ? "s" : ""}</p>
        {pinOk ? (
          <Button size="sm" variant="outline" data-testid={`add-plan-${defectId || "project"}`} onClick={() => setCreating((v) => !v)} className="h-7 gap-1 border-white/15 bg-transparent text-xs hover:bg-white/5"><Plus className="w-3 h-3" /> New plan</Button>
        ) : (
          <button onClick={onNeedPin} className="text-xs text-[#C5A24A] hover:underline" data-testid="unlock-hint">Enter access code to edit</button>
        )}
      </div>

      {mine.length === 0 && !creating && <p className="text-xs text-muted-foreground mt-2">No action plan yet.</p>}
      {mine.map((p) => <PlanCard key={p.id} plan={p} token={token} pin={pin} pinOk={pinOk} onRefresh={onRefresh} />)}

      {creating && (
        <div className="mt-3 space-y-2 bg-[#0A0A0A] border border-white/10 rounded p-3">
          <input placeholder="Plan title (e.g. Seal & re-waterproof deck)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="plan-title-input" />
          <textarea placeholder="What needs to be done" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} min-h-[60px]`} data-testid="plan-desc-input" />
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Assignee name" value={form.assignee_name} onChange={(e) => setForm({ ...form, assignee_name: e.target.value })} className={inputCls} data-testid="plan-assignee-input" />
            <input placeholder="Assignee email" value={form.assignee_email} onChange={(e) => setForm({ ...form, assignee_email: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Target date</label>
            <input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className={inputCls} data-testid="plan-date-input" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={createPlan} disabled={busy} className="bg-[#C5A24A] hover:bg-[#A8863A] text-black text-xs" data-testid="save-plan-btn">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Create plan"}</Button>
            <Button size="sm" variant="ghost" onClick={() => setCreating(false)} className="text-xs">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
